import { Component, useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  Trophy, Crown, Award, Activity, Footprints, Mountain, Bike,
  Flame, Compass, Users, Goal, Plus, X, Sunrise, CheckCircle2, TrendingUp,
} from 'lucide-react';
import {
  createGoal, deleteGoal, fetchGoals, fetchMyActivities, fetchRanking,
} from '../../api/move';
import type {
  GoalPeriod, GoalType, MoveActivity, MoveGoal, MoveType,
  RankingData, RankingEntry, RankingPeriod,
} from '../../api/move';
import { API_BASE } from '../../config';
import styles from '../../styles/Move.module.css';

type RankingView = 'ranking' | 'stats';

class StatsErrorBoundary extends Component<{ children: ReactNode }, { message: string | null }> {
  state: { message: string | null } = { message: null };

  static getDerivedStateFromError(error: unknown) {
    return { message: error instanceof Error ? error.message : 'Error pintando stats personales' };
  }

  render() {
    if (this.state.message) {
      return (
        <section className={styles.section} style={{ marginTop: '-3rem', position: 'relative', zIndex: 5 }}>
          <div className={styles.card}>
            <p style={{ margin: 0, color: 'hsl(348 90% 45%)', fontSize: 13 }}>
              {this.state.message}
            </p>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

const PERIODS: { key: RankingPeriod; label: string }[] = [
  { key: 'weekly', label: 'Semanal' },
  { key: 'monthly', label: 'Mensual' },
  { key: 'all', label: 'Histórico' },
];

const GOAL_TYPES: { key: GoalType; label: string; unit: string | null; icon: ReactNode }[] = [
  { key: 'distance', label: 'Km', unit: 'km', icon: <Mountain size={16} /> },
  { key: 'sessions', label: 'Sesiones', unit: 'sesiones', icon: <Activity size={16} /> },
  { key: 'steps', label: 'Días pasos', unit: 'días', icon: <Footprints size={16} /> },
  { key: 'elevation', label: 'Desnivel', unit: 'm', icon: <TrendingUp size={16} /> },
  { key: 'custom', label: 'Libre', unit: null, icon: <Goal size={16} /> },
];

const GOAL_PERIODS: { key: GoalPeriod; label: string }[] = [
  { key: 'weekly', label: 'Semana' },
  { key: 'monthly', label: 'Mes' },
  { key: 'all', label: 'Histórico' },
  { key: 'custom', label: 'Fechas' },
];

function avatarUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/profile_pic/${filename}`;
}

function initials(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
}

function firstName(name?: string) {
  return (name || '').trim().split(/\s+/)[0] || '';
}

function formatPoints(n: number) {
  return Number(n || 0).toLocaleString('es-ES');
}

function goalTargetPlaceholder(type: GoalType) {
  if (type === 'steps') return 'Ej. 5';
  if (type === 'sessions') return 'Ej. 4';
  if (type === 'elevation') return 'Ej. 800';
  if (type === 'distance') return 'Ej. 40';
  return 'Opcional';
}

function asNumber(value: unknown) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function calcStreak(activities: MoveActivity[]) {
  const days = new Set(
    activities
      .map(a => new Date(a.fecha))
      .filter(d => Number.isFinite(d.getTime()))
      .map(d => d.toDateString())
  );
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  while (days.has(cursor.toDateString())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function typeLabel(type: MoveType) {
  if (type === 'run') return 'Running';
  if (type === 'bike') return 'Ciclismo';
  if (type === 'walk') return 'Trekking';
  return 'Mixto';
}

function StatsIcon({ type }: { type: MoveType }) {
  if (type === 'bike') return <Bike size={22} />;
  if (type === 'walk') return <Mountain size={22} />;
  return <Footprints size={22} />;
}

function Avatar({ name, src, size = 40, ring = false, ringColor = '#fff' }: {
  name: string; src: string | null; size?: number; ring?: boolean; ringColor?: string;
}) {
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    background: 'var(--move-grad-cool)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size * 0.32,
    fontWeight: 700,
    flexShrink: 0,
    boxShadow: ring ? `0 0 0 4px ${ringColor}, var(--move-shadow-md)` : 'var(--move-shadow-md)',
  };
  return (
    <div style={style}>
      {src ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span>{initials(name)}</span>}
    </div>
  );
}

export default function RankingPage() {
  const [view, setView] = useState<RankingView>('ranking');
  const [period, setPeriod] = useState<RankingPeriod>('weekly');
  const [data, setData] = useState<RankingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activities, setActivities] = useState<MoveActivity[]>([]);
  const [goals, setGoals] = useState<MoveGoal[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [goalSheetOpen, setGoalSheetOpen] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalType, setGoalType] = useState<GoalType>('distance');
  const [goalPeriod, setGoalPeriod] = useState<GoalPeriod>('monthly');
  const [goalTarget, setGoalTarget] = useState('');
  const [goalStart, setGoalStart] = useState('');
  const [goalEnd, setGoalEnd] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalError, setGoalError] = useState<string | null>(null);

  useEffect(() => {
    if (view !== 'ranking') return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await fetchRanking(period);
        setData(d);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Error cargando ranking');
      } finally {
        setLoading(false);
      }
    })();
  }, [period, view]);

  useEffect(() => {
    if (view !== 'stats') return;
    (async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const list = await fetchMyActivities().catch(() => []);
        setActivities(Array.isArray(list) ? list : []);
        const currentGoals = await fetchGoals().catch(() => []);
        setGoals(Array.isArray(currentGoals) ? currentGoals : []);
      } catch (e: any) {
        setStatsError(e?.response?.data?.error || 'Error cargando stats personales');
      } finally {
        setStatsLoading(false);
      }
    })();
  }, [view]);

  const top = data?.top ?? [];
  const visibleTop = top.slice(0, 10);
  const top3 = visibleTop.slice(0, 3);
  const rest = visibleTop;
  const you = data?.you ?? null;

  // visual order: 2nd, 1st, 3rd
  const podium: (RankingEntry | undefined)[] = [top3[1], top3[0], top3[2]];
  const positions = [2, 1, 3];

  async function reloadGoals() {
    const nextGoals = await fetchGoals();
    setGoals(nextGoals);
  }

  async function handleCreateGoal() {
    setGoalError(null);
    const title = goalTitle.trim();
    const target = parseInt(goalTarget, 10);
    if (!title) {
      setGoalError('Pon un título para el objetivo');
      return;
    }
    if (goalType !== 'custom' && (!Number.isFinite(target) || target <= 0)) {
      setGoalError('Pon una meta numérica');
      return;
    }
    setSavingGoal(true);
    try {
      const selectedType = GOAL_TYPES.find(item => item.key === goalType);
      await createGoal({
        titulo: title,
        tipo: goalType,
        target_value: goalType === 'custom' ? null : target,
        unit: selectedType?.unit ?? null,
        period: goalPeriod,
        starts_at: goalStart || null,
        ends_at: goalEnd || null,
      });
      await reloadGoals();
      setGoalTitle('');
      setGoalTarget('');
      setGoalStart('');
      setGoalEnd('');
      setGoalSheetOpen(false);
    } catch (e: any) {
      setGoalError(e?.response?.data?.error || 'No se pudo crear el objetivo');
    } finally {
      setSavingGoal(false);
    }
  }

  async function handleDeleteGoal(goalId: number) {
    setSavingGoal(true);
    try {
      await deleteGoal(goalId);
      await reloadGoals();
    } finally {
      setSavingGoal(false);
    }
  }

  return (
    <div className={`move-app ${styles.page}`} style={{ paddingTop: 0 }}>
      {/* Hero oscuro */}
      <header className={styles.heroDark}>
        <div className={styles.heroDarkBg} />
        <div className={styles.heroDarkOrbA} />
        <div className={styles.heroDarkOrbB} />
        <div className={styles.heroDarkInner}>
          <div className={styles.heroDarkTitleRow}>
            <Trophy size={20} color="hsl(42 95% 55%)" />
            <h1 className={styles.heroDarkTitle}>Ranking</h1>
          </div>
          <p className={styles.heroDarkSubtitle}>Compite con la comunidad</p>

          <div className={styles.glassDark}>
            <button
              onClick={() => setView('ranking')}
              className={`${styles.periodBtn} ${view === 'ranking' ? styles.periodBtnActive : ''}`}
            >
              Ranking
            </button>
            <button
              onClick={() => setView('stats')}
              className={`${styles.periodBtn} ${view === 'stats' ? styles.periodBtnActive : ''}`}
            >
              Stats personales
            </button>
          </div>

          {view === 'ranking' && (
          <div className={styles.glassDark}>
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`${styles.periodBtn} ${period === p.key ? styles.periodBtnActive : ''}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          )}
        </div>
      </header>

      {view === 'stats' && (
        <StatsErrorBoundary>
          <PersonalStats
            activities={activities}
            goals={goals}
            loading={statsLoading}
            error={statsError}
            goalSheetOpen={goalSheetOpen}
            goalTitle={goalTitle}
            goalType={goalType}
            goalPeriod={goalPeriod}
            goalTarget={goalTarget}
            goalStart={goalStart}
            goalEnd={goalEnd}
            savingGoal={savingGoal}
            goalError={goalError}
            onGoalSheetOpenChange={setGoalSheetOpen}
            onGoalTitleChange={setGoalTitle}
            onGoalTypeChange={setGoalType}
            onGoalPeriodChange={setGoalPeriod}
            onGoalTargetChange={setGoalTarget}
            onGoalStartChange={setGoalStart}
            onGoalEndChange={setGoalEnd}
            onCreateGoal={handleCreateGoal}
            onDeleteGoal={handleDeleteGoal}
          />
        </StatsErrorBoundary>
      )}

      {view === 'ranking' && (
        <>
      {loading && <div className={styles.empty}>Cargando…</div>}
      {error && <div className={styles.empty}>{error}</div>}

      {!loading && !error && top.length === 0 && (
        <section className={styles.section} style={{ marginTop: -64, position: 'relative', zIndex: 5 }}>
          <div className={styles.card} style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <Trophy size={36} color="hsl(42 95% 55%)" style={{ marginBottom: 12 }} />
            <p style={{ fontFamily: 'Sora, Inter, sans-serif', fontWeight: 700, fontSize: 15, margin: 0 }}>
              Aún no hay actividad
            </p>
            <p style={{ fontSize: 12, color: 'var(--move-muted-fg)', marginTop: 6 }}>
              Registra tu primera actividad para entrar en el ranking
            </p>
          </div>
        </section>
      )}

      {!loading && !error && top.length > 0 && (
        <>
          {/* Podium */}
          <div className={styles.podium}>
            {podium.map((entry, i) => {
              const pos = positions[i];
              const isFirst = pos === 1;
              const blockClass = isFirst ? styles.podiumBlock1 : pos === 2 ? styles.podiumBlock2 : styles.podiumBlock3;
              const posClass = pos === 1 ? styles.posGold : pos === 2 ? styles.posSilver : styles.posBronze;

              if (!entry) {
                return (
                  <div key={`empty-${pos}`} className={styles.podiumCol}>
                    <div style={{ width: isFirst ? 80 : 64, height: isFirst ? 80 : 64, borderRadius: '50%', background: 'var(--move-muted)', marginBottom: 8 }} />
                    <p className={styles.podiumName} style={{ color: 'var(--move-muted-fg)' }}>—</p>
                    <p className={styles.podiumPoints}>0</p>
                    <div className={`${styles.podiumBlock} ${blockClass}`}>
                      <span className={styles.podiumBlockNumber}>{pos}</span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={entry.user_id} className={styles.podiumCol}>
                  <div className={styles.podiumAvatarWrap}>
                    {isFirst && <Crown className={styles.podiumCrown} size={20} />}
                    <Avatar
                      name={entry.fullName}
                      src={avatarUrl(entry.profile_pic)}
                      size={isFirst ? 80 : 64}
                      ring
                      ringColor={isFirst ? 'hsl(42 95% 55%)' : '#fff'}
                    />
                    <div className={`${styles.podiumPosBadge} ${posClass}`}>{pos}</div>
                  </div>
                  <p className={styles.podiumName}>{firstName(entry.fullName)}</p>
                  <p className={styles.podiumPoints}>{formatPoints(entry.puntos)}</p>
                  <div className={`${styles.podiumBlock} ${blockClass}`}>
                    <span className={styles.podiumBlockNumber}>{pos}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tu posición */}
          {you && (
            <section className={styles.section} style={{ marginTop: '1.25rem' }}>
              <div className={styles.youCard}>
                <div className={styles.youPosBadge}>{you.position}</div>
                <Avatar name={you.fullName} src={avatarUrl(you.profile_pic)} size={40} />
                <div className={styles.youInfo}>
                  <p className={styles.youName}>Tú · {firstName(you.fullName)}</p>
                  <p className={styles.youMeta}>
                    {you.sessions} sesiones · {(you.total_m / 1000).toFixed(1)} km · {you.step_goal_days} días objetivo
                  </p>
                </div>
                <div className={styles.youPoints}>
                  <p className={styles.youPointsValue}>{formatPoints(you.puntos)}</p>
                </div>
              </div>
            </section>
          )}

          {/* Resto de la clasificación */}
          {rest.length > 0 && (
            <section className={styles.section} style={{ marginTop: '1.25rem' }}>
              <div className={styles.uppercaseLabel}>
                <Award size={14} />
                <span>Top 10</span>
              </div>
              <div className={styles.cardList} style={{ gap: 8 }}>
                {rest.map(entry => (
                  <div
                    key={entry.user_id}
                    className={`${styles.rankRow} ${entry.isYou ? styles.rankRowYou : ''}`}
                  >
                    <span className={styles.rankPosition}>{entry.position}</span>
                    <Avatar name={entry.fullName} src={avatarUrl(entry.profile_pic)} size={40} />
                    <div className={styles.rankInfo}>
                      <p className={styles.rankName}>{entry.fullName}</p>
                      <p className={styles.rankSub}>
                        {entry.sessions} sesiones · {(entry.total_m / 1000).toFixed(1)} km · {entry.step_goal_days} días objetivo
                      </p>
                    </div>
                    <p className={styles.rankPoints}>{formatPoints(entry.puntos)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}

function PersonalStats({
  activities,
  goals,
  loading,
  error,
  goalSheetOpen,
  goalTitle,
  goalType,
  goalPeriod,
  goalTarget,
  goalStart,
  goalEnd,
  savingGoal,
  goalError,
  onGoalSheetOpenChange,
  onGoalTitleChange,
  onGoalTypeChange,
  onGoalPeriodChange,
  onGoalTargetChange,
  onGoalStartChange,
  onGoalEndChange,
  onCreateGoal,
  onDeleteGoal,
}: {
  activities: MoveActivity[];
  goals: MoveGoal[];
  loading: boolean;
  error: string | null;
  goalSheetOpen: boolean;
  goalTitle: string;
  goalType: GoalType;
  goalPeriod: GoalPeriod;
  goalTarget: string;
  goalStart: string;
  goalEnd: string;
  savingGoal: boolean;
  goalError: string | null;
  onGoalSheetOpenChange: (value: boolean) => void;
  onGoalTitleChange: (value: string) => void;
  onGoalTypeChange: (value: GoalType) => void;
  onGoalPeriodChange: (value: GoalPeriod) => void;
  onGoalTargetChange: (value: string) => void;
  onGoalStartChange: (value: string) => void;
  onGoalEndChange: (value: string) => void;
  onCreateGoal: () => void;
  onDeleteGoal: (goalId: number) => void;
}) {
  if (loading) return <div className={styles.empty}>Cargando…</div>;
  if (error) return <div className={styles.empty}>{error}</div>;

  const safeActivities = Array.isArray(activities) ? activities : [];
  const safeGoals = Array.isArray(goals) ? goals : [];
  const sessions = safeActivities.length;
  const totalMeters = safeActivities.reduce((sum, item) => sum + asNumber(item.distancia_m), 0);
  const totalElevation = safeActivities.reduce((sum, item) => sum + asNumber(item.desnivel_m), 0);
  const completedGoals = safeGoals.filter(goal => Boolean(goal.completed_at)).length;
  const streak = calcStreak(safeActivities);
  const distinctRoutes = new Set(safeActivities.map(a => a.route_id).filter(Boolean)).size;
  const hasCommunity = safeActivities.some(a => a.event_id);
  const hasEarly = safeActivities.some(a => {
    const d = new Date(a.fecha);
    return Number.isFinite(d.getTime()) && d.getHours() < 8;
  });
  const counts = safeActivities.reduce<Record<MoveType, number>>((acc, item) => {
    const type = ['walk', 'run', 'bike', 'mixed'].includes(item.tipo) ? item.tipo : 'mixed';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, { walk: 0, run: 0, bike: 0, mixed: 0 });
  const preferred = (Object.entries(counts) as [MoveType, number][])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([type]) => type);

  const achievements = [
    { title: 'Primer paso', label: 'COMUN', icon: <Footprints size={24} />, color: 'green', done: sessions >= 1, progress: Math.min(100, sessions * 100) },
    { title: 'Madrugador', label: 'RARA', icon: <Sunrise size={24} />, color: 'orange', done: hasEarly, progress: hasEarly ? 100 : 0 },
    { title: 'Racha de fuego', label: 'EPICA', icon: <Flame size={24} />, color: 'pink', done: streak >= 3, progress: Math.min(100, (streak / 3) * 100) },
    { title: 'Explorador', label: '60%', icon: <Compass size={24} />, color: 'gray', done: distinctRoutes >= 3, progress: Math.min(100, (distinctRoutes / 3) * 100) },
    { title: 'Comunidad', label: '80%', icon: <Users size={24} />, color: 'gray', done: hasCommunity, progress: hasCommunity ? 100 : 80 },
    { title: 'Leyenda Segoviana', label: '25%', icon: <Crown size={24} />, color: 'gray', done: totalMeters >= 100000, progress: Math.min(100, (totalMeters / 100000) * 100) },
  ];
  const unlocked = achievements.filter(a => a.done).length;

  return (
    <>
      <section className={styles.personalStatsGrid}>
        <div className={styles.personalStatCard}>
          <Activity size={20} />
          <strong>{sessions}</strong>
          <span>Sesiones</span>
        </div>
        <div className={styles.personalStatCard}>
          <Goal size={20} />
          <strong>{completedGoals}</strong>
          <span>Objetivos</span>
        </div>
        <div className={styles.personalStatCard}>
          <Mountain size={20} />
          <strong>{formatPoints(totalElevation)} m</strong>
          <span>Desnivel</span>
        </div>
        <div className={styles.personalStatCard}>
          <Trophy size={20} />
          <strong>{unlocked}</strong>
          <span>Logros</span>
        </div>
      </section>

      <section className={styles.section} style={{ marginTop: '1.25rem' }}>
        <h2 className={styles.sectionTitle} style={{ marginBottom: 12 }}>Deportes preferidos</h2>
        <div className={styles.sportsGrid}>
          {(preferred.length ? preferred : ['run', 'walk', 'bike'] as MoveType[]).map(type => (
            <div key={type} className={styles.sportPill}>
              <StatsIcon type={type} />
              <span>{typeLabel(type)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section} style={{ marginTop: '1.25rem' }}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Logros</h2>
          <span className={styles.sectionLink}>{unlocked}/6</span>
        </div>
        <div className={styles.achievementGrid}>
          {achievements.map(item => (
            <div key={item.title} className={`${styles.achievementCard} ${!item.done ? styles.achievementLocked : ''}`}>
              <div className={`${styles.achievementIcon} ${styles[`achievement${item.color}`]}`}>{item.icon}</div>
              <strong>{item.title}</strong>
              {item.done ? (
                <span className={styles.achievementBadge}>{item.label}</span>
              ) : (
                <div className={styles.achievementProgress}>
                  <div style={{ width: `${item.progress}%` }} />
                  <span>{Math.round(item.progress)}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section} style={{ marginTop: '1.25rem' }}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Objetivos personales</h2>
          <button
            type="button"
            className={styles.sectionLink}
            style={{ border: 0, background: 'transparent', padding: 0 }}
            onClick={() => onGoalSheetOpenChange(true)}
          >
            <Plus size={12} /> Crear
          </button>
        </div>
        <div className={styles.cardList} style={{ gap: 10 }}>
          {safeGoals.length === 0 && <p className={styles.goalEmpty}>Aún no has marcado objetivos</p>}
          {safeGoals.map(goal => (
            <GoalCard
              key={goal.goal_id}
              goal={goal}
              disabled={savingGoal}
              onDelete={() => onDeleteGoal(goal.goal_id)}
            />
          ))}
        </div>
      </section>

      {goalSheetOpen && (
        <div
          onClick={() => onGoalSheetOpenChange(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--move-card)',
              width: '100%', maxWidth: 480,
              borderTopLeftRadius: 24, borderTopRightRadius: 24,
              padding: 20, paddingBottom: 'max(env(safe-area-inset-bottom), 20px)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontFamily: 'Sora, Inter, sans-serif', fontWeight: 700, fontSize: 18, margin: 0 }}>
                Nuevo objetivo
              </h3>
              <button
                onClick={() => onGoalSheetOpenChange(false)}
                style={{ background: 'var(--move-muted)', border: 0, width: 32, height: 32, borderRadius: 999, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            <div className={styles.goalFormSheet}>
              <div>
                <label className={styles.label}>Título</label>
                <input
                  className={styles.input}
                  value={goalTitle}
                  onChange={e => onGoalTitleChange(e.target.value)}
                  placeholder="Ej. 40 km este mes"
                />
              </div>

              <div>
                <label className={styles.label}>Tipo</label>
                <div className={styles.goalTypePillRow}>
                  {GOAL_TYPES.map(type => (
                    <button
                      key={type.key}
                      type="button"
                      className={`${styles.goalTypePill} ${goalType === type.key ? styles.goalTypePillActive : ''}`}
                      onClick={() => {
                        onGoalTypeChange(type.key);
                        if (type.key === 'custom') onGoalTargetChange('');
                      }}
                    >
                      {type.icon}
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.goalFormRow}>
                <div>
                  <label className={styles.label}>Meta</label>
                  <input
                    className={styles.input}
                    type="number"
                    inputMode="numeric"
                    value={goalTarget}
                    onChange={e => onGoalTargetChange(e.target.value)}
                    placeholder={goalTargetPlaceholder(goalType)}
                    disabled={goalType === 'custom'}
                  />
                </div>
                <div>
                  <label className={styles.label}>Periodo</label>
                  <select
                    className={styles.input}
                    value={goalPeriod}
                    onChange={e => onGoalPeriodChange(e.target.value as GoalPeriod)}
                  >
                    {GOAL_PERIODS.map(period => (
                      <option key={period.key} value={period.key}>{period.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {goalPeriod === 'custom' && (
                <div className={styles.goalFormRow}>
                  <div>
                    <label className={styles.label}>Inicio</label>
                    <input className={styles.input} type="date" value={goalStart} onChange={e => onGoalStartChange(e.target.value)} />
                  </div>
                  <div>
                    <label className={styles.label}>Fin</label>
                    <input className={styles.input} type="date" value={goalEnd} onChange={e => onGoalEndChange(e.target.value)} />
                  </div>
                </div>
              )}

              {goalError && <p style={{ color: 'hsl(348 90% 50%)', margin: 0, fontSize: 13 }}>{goalError}</p>}

              <button
                className={styles.btnPrimary}
                style={{ width: '100%', marginTop: 4 }}
                onClick={onCreateGoal}
                disabled={savingGoal}
              >
                {savingGoal ? 'Guardando…' : 'Crear objetivo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function goalIconClass(type: GoalType) {
  if (type === 'distance') return styles.goalIconDistance;
  if (type === 'sessions') return styles.goalIconSessions;
  if (type === 'steps') return styles.goalIconSteps;
  if (type === 'elevation') return styles.goalIconElevation;
  return styles.goalIconCustom;
}

function goalIcon(type: GoalType) {
  if (type === 'distance') return <Mountain size={18} />;
  if (type === 'sessions') return <Activity size={18} />;
  if (type === 'steps') return <Footprints size={18} />;
  if (type === 'elevation') return <TrendingUp size={18} />;
  return <Goal size={18} />;
}

function goalPeriodLabel(period: GoalPeriod) {
  return GOAL_PERIODS.find(item => item.key === period)?.label || period;
}

function goalUnitLabel(goal: MoveGoal) {
  if (goal.tipo === 'distance') return 'km';
  if (goal.tipo === 'sessions') return 'sesiones';
  if (goal.tipo === 'steps') return 'días';
  if (goal.tipo === 'elevation') return 'm';
  return goal.unit || '';
}

function GoalCard({ goal, disabled, onDelete }: { goal: MoveGoal; disabled: boolean; onDelete: () => void }) {
  const safeType = GOAL_TYPES.some(item => item.key === goal.tipo) ? goal.tipo : 'custom';
  const safePeriod = GOAL_PERIODS.some(item => item.key === goal.period) ? goal.period : 'custom';
  const target = asNumber(goal.target_value);
  const current = asNumber(goal.current_value);
  const progress = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  const completed = Boolean(goal.completed_at) || (target > 0 && current >= target);
  const unit = goalUnitLabel(goal);
  const startsAt = goal.starts_at ? String(goal.starts_at).slice(0, 10) : '';
  const endsAt = goal.ends_at ? String(goal.ends_at).slice(0, 10) : '';

  return (
    <div className={`${styles.goalCard} ${completed ? styles.goalCardCompleted : ''}`}>
      {completed && (
        <span className={styles.goalCompletedBadge}>
          <CheckCircle2 size={12} /> Hecho
        </span>
      )}
      <div className={styles.goalCardHead}>
        <div className={`${styles.goalIconBox} ${goalIconClass(safeType)}`}>{goalIcon(safeType)}</div>
        <div className={styles.goalTitleCol}>
          <p className={styles.goalTitleText}>{goal.titulo || 'Objetivo personal'}</p>
          <p className={styles.goalSubText}>
            {goalPeriodLabel(safePeriod)}
            {startsAt && endsAt ? ` · ${startsAt} - ${endsAt}` : ''}
          </p>
        </div>
        <button className={styles.goalDeleteBtn} onClick={onDelete} disabled={disabled} aria-label="Eliminar objetivo">
          <X size={15} />
        </button>
      </div>

      {safeType !== 'custom' && (
        <>
          <div className={styles.goalProgressRow}>
            <span>{formatPoints(current)} {unit}</span>
            <span>{target > 0 ? `${formatPoints(target)} ${unit}` : 'Sin meta'}</span>
          </div>
          <div className={styles.goalProgressBar}>
            <div
              className={`${styles.goalProgressFill} ${completed ? styles.goalProgressFillCompleted : ''}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </>
      )}
    </div>
  );
}
